/**
 * Split payment at the register: ONE order paid with several cards and cash,
 * one payment at a time (owner, Oct 29 2026 - "as simple as the main app"):
 *
 *   Split -> choose how the customer pays -> amount (prefilled with what remains;
 *   Half / All; Even split / By item only prefill it) -> Charge / Take cash ->
 *   the next payment ... until REMAINING is $0.00 -> paid -> receipt.
 *
 * The flow itself is the shared SplitPayFlow (the dining check uses it too); this
 * file is the register's adapter (/api/checkout/orders/{id}/split, mode "next",
 * payments/split_tender.py) and the "leave with a balance due" page. Nothing here
 * decides money: a payment is paid only on Aster Gateway's word, REMAINING is the
 * server's. An order left part-paid keeps its balance due ("Balance due" on the
 * register); a manager can void the approved cards.
 */
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { RefundBody } from '@/apps/dining/components/CheckoutExtras';
import SplitPayFlow, { registerView } from '@/apps/dining/components/SplitPayFlow';
import { deviceId } from '@/apps/dining/lib/api';

const API = process.env.REACT_APP_BACKEND_URL;
const usd = (c) => `$${((c || 0) / 100).toFixed(2)}`;

async function call(path, token, options = {}) {
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'X-Device-ID': deviceId(), ...(options.headers || {}) },
  });
  const body = await res.json().catch(() => null);
  if (!res.ok || !body) {
    const d = body?.detail;
    throw new Error(typeof d === 'string' ? d : (d?.message || 'The server is unavailable right now'));
  }
  return body;
}

/** The ways this register takes money: card when the restaurant is connected, cash when it is on,
 *  Zelle / Venmo / Crypto only when the owner set them up (their QR in Payment settings). */
export function registerTenders(settings, qr) {
  const out = [];
  if (!settings || settings.card_enabled !== false) out.push({ id: 'card', label: 'Card' });
  if (!settings || settings.cash_enabled !== false) out.push({ id: 'cash', label: 'Cash' });
  if (qr?.zelle_qr_url) out.push({ id: 'zelle', label: 'Zelle' });
  if (qr?.venmo_qr_url) out.push({ id: 'venmo', label: 'Venmo' });
  if (qr?.crypto_qr_url) out.push({ id: 'crypto', label: 'Crypto' });
  return out;
}

const SplitPaymentPanel = forwardRef(function SplitPaymentPanel({
  order, token, terminals, terminalId, onTerminal, tipsEnabled, tipPresets = [], tipOnTerminal, settings, qr,
  canManage, onPaid, onLeave, onCancel, onWaitingCard,
}, ref) {
  const orderId = order.order_id;
  const flow = useRef(null);
  const [leaving, setLeaving] = useState(null);

  const adapter = useMemo(() => {
    const base = `/api/checkout/orders/${orderId}/split`;
    const read = async () => registerView(await call(base, token()));
    return {
      load: read,
      next: async (body) => registerView(await call(base, token(), { method: 'POST', body: JSON.stringify(body) })),
      card: async (n) => {
        const a = await call(`${base}/parts/${n}/card`, token(), { method: 'POST', body: JSON.stringify({ terminal_id: terminalId }) });
        return { view: registerView(a), payment: a.payment };
      },
      poll: async (paymentId) => {
        const payment = await call(`/api/checkout/payments/${paymentId}`, token());
        return { view: payment.is_final ? await read() : null, payment };
      },
      cash: async (n, tenderedCents) => {
        const a = await call(`${base}/parts/${n}/cash`, token(), { method: 'POST', body: JSON.stringify({ tendered_cents: tenderedCents }) });
        return { view: registerView(a), change: a.change_cents || 0 };
      },
      manual: async (n, method) => registerView(await call(`${base}/parts/${n}/manual`, token(), { method: 'POST', body: JSON.stringify({ method }) })),
      cancelCard: (paymentId) => call(`/api/checkout/payments/${paymentId}/cancel`, token(), { method: 'POST' }),
      cancelSplit: async () => registerView(await call(base, token(), { method: 'DELETE' })),
    };
  }, [orderId, token, terminalId]);

  useImperativeHandle(ref, () => ({
    requestClose() {
      if (leaving) return false;
      return flow.current ? flow.current.requestClose() : false;
    },
  }), [leaving]);

  useEffect(() => { setLeaving(null); }, [orderId]);

  if (leaving) {
    return (
      <div className="space-y-4" data-testid="split-leave">
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
          <p className="text-amber-300 text-sm font-semibold uppercase tracking-wide">Part-paid order</p>
          <p className="text-white text-3xl font-bold mt-1" data-testid="split-leave-due">{usd(leaving.due)} balance due</p>
          <p className="text-slate-300 text-sm mt-2 leading-relaxed">
            Order #{orderId} keeps the {usd(leaving.paid)} already paid. It is not sent to the kitchen until the balance is paid.
            Resume it from <b>Balance due</b> on the register, or a manager voids the approved cards below.
          </p>
        </div>
        {canManage ? (
          <RefundBody dark scope={{ kind: 'order', id: orderId }} onChanged={() => {}} />
        ) : (
          <p className="text-slate-400 text-xs">A manager can void the approved cards (Refund or void, owner / manager sign-in).</p>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Button variant="outline" className="h-12 border-slate-600 text-slate-200" onClick={() => setLeaving(null)} data-testid="split-leave-back">Back to payment</Button>
          <Button className="h-12 bg-amber-600 hover:bg-amber-700" onClick={() => onLeave?.(leaving)} data-testid="split-leave-confirm">Leave balance due</Button>
        </div>
      </div>
    );
  }

  const terminal = terminals.find((t) => t.id === terminalId);
  return (
    <div className="space-y-3">
      {terminals.length > 1 && (
        <div className="flex gap-1.5 flex-wrap" data-testid="split-terminals">
          {terminals.map((t) => (
            <button key={t.id} type="button" onClick={() => onTerminal(t.id)}
              className={`px-3 h-9 rounded-lg border text-xs ${terminalId === t.id ? 'border-blue-500 bg-blue-500/10 text-white' : 'border-slate-700 text-slate-300'}`}>
              {t.station || t.name || `Terminal ${t.hub_terminal_id}`}
            </button>
          ))}
        </div>
      )}
      <SplitPayFlow
        ref={flow}
        dark
        adapter={adapter}
        tenders={registerTenders(settings, qr)}
        tips={{ enabled: !!tipsEnabled, presets: tipPresets, onTerminal: !!tipOnTerminal }}
        cardReady={!!terminalId}
        cardHint={terminals.length === 0 ? 'No card terminal is assigned to the POS register' : 'Choose the terminal at this register'}
        terminalLabel={terminal ? (terminal.station || terminal.name) : undefined}
        onPaid={(view) => {
          const last = [...view.payments].reverse().find((p) => p.label.includes('••••'));
          onPaid?.({ card_type: 'SPLIT', last_four: last ? last.label.slice(-4) : '', reference: `#${orderId}` }, view);
        }}
        onLeave={(view) => setLeaving(view)}
        onClose={() => onCancel?.()}
        onWaitingCard={onWaitingCard}
      />
    </div>
  );
});

export default SplitPaymentPanel;
